import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { listingsApi, reviewsApi, categoriesApi, getPhotoUrl, FALLBACK_LISTING_IMAGE } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import CalendarBookingWidget from '../components/CalendarBookingWidget';
import ReviewsSection from '../components/ReviewsSection';
import LocationMap from '../components/LocationMap';
import { MapPin, Users, Star, Clock, CheckCircle, House } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Category-specific fallback images
const CATEGORY_LISTING_IMAGES = {
  'reading-room': 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'reading_room': 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'workspace': 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
  'home-gym': 'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=800&q=80',
  'home_gym': 'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=800&q=80',
  'home-theater': 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80',
  'home_theater': 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80',
  'game-room': 'https://images.unsplash.com/photo-1511882150382-421056c89033?w=800&q=80',
  'game_room': 'https://images.unsplash.com/photo-1511882150382-421056c89033?w=800&q=80',
  'backyard': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
  'pool': 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=800&q=80',
  'dining': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
  'private_dining': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
  'hobby-room': 'https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=800&q=80',
  'wellness': 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80',
  'creator-studio': 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80',
  'creative_studio': 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80',
  'outdoor-sports': 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&q=80',
  'outdoor_sports': 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&q=80',
  'default': 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80',
};

const getCategoryFallbackImage = (category) => {
  return CATEGORY_LISTING_IMAGES[category] || CATEGORY_LISTING_IMAGES['default'];
};

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  useEffect(() => {
    // Scroll to top when navigating to this page
    window.scrollTo(0, 0);
    loadListing();
    loadReviews();
    loadCategories();
  }, [id]);

  const loadListing = async () => {
    try {
      const response = await listingsApi.getById(id, token);
      setListing(response.data);
    } catch (error) {
      console.error('Failed to load listing:', error);
      toast.error('Failed to load listing');
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const response = await reviewsApi.getByListing(id);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleBookingSuccess = () => {
    navigate('/guest/bookings');
  };

  const getCategoryName = (categorySlug) => {
    const category = categories.find(c => c.slug === categorySlug);
    return category ? category.name : categorySlug;
  };

  const isOwnListing = user && listing && listing.host_id === user.id;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-zinc-400">Loading listing...</div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <House size={64} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
          <h2 className="text-2xl text-white mb-2">Listing Not Found</h2>
          <p className="text-zinc-500 mb-6">This space may no longer be available</p>
          <Button onClick={() => navigate('/')} className="bg-[#D4AF37] text-black">
            Browse Spaces
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Photo Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Main Photo */}
          <div className="aspect-[4/3] relative">
            {listing.photos && listing.photos.length > 0 ? (
              <img
                src={getPhotoUrl(listing.photos[activePhotoIndex])}
                alt={listing.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = getCategoryFallbackImage(listing.category);
                }}
              />
            ) : (
              <img
                src={getCategoryFallbackImage(listing.category)}
                alt={listing.title}
                className="w-full h-full object-cover"
              />
            )}
          </div>
          
          {/* Thumbnail Grid */}
          {listing.photos && listing.photos.length > 1 && (
            <div className="grid grid-cols-2 gap-4">
              {listing.photos.slice(1, 5).map((photo, index) => (
                <button
                  key={index}
                  onClick={() => setActivePhotoIndex(index + 1)}
                  className={`aspect-[4/3] relative overflow-hidden ${
                    activePhotoIndex === index + 1 ? 'ring-2 ring-[#D4AF37]' : ''
                  }`}
                >
                  <img
                    src={getPhotoUrl(photo)}
                    alt={`${listing.title} ${index + 2}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = getCategoryFallbackImage(listing.category);
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm text-[#D4AF37] bg-[#D4AF37]/10 px-3 py-1 rounded-full">
                  {getCategoryName(listing.category)}
                </span>
                {listing.average_rating > 0 && (
                  <div className="flex items-center gap-1 text-sm">
                    <Star size={16} weight="fill" className="text-[#D4AF37]" />
                    <span className="text-white">{listing.average_rating.toFixed(1)}</span>
                    <span className="text-zinc-500">({listing.total_reviews} reviews)</span>
                  </div>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-serif text-white mb-3">{listing.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-zinc-400">
                <div className="flex items-center gap-2">
                  <MapPin size={20} weight="duotone" />
                  <span>{listing.location_city}, {listing.location_zip}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users size={20} weight="duotone" />
                  <span>Up to {listing.max_guests} guests</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={20} weight="duotone" />
                  <span>{listing.slot_duration || 60} min slots</span>
                </div>
              </div>
            </div>

            {/* Host Info */}
            <div className="bg-[#121212] border border-white/10 p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <span className="text-xl font-serif text-[#D4AF37]">
                    {listing.host_name?.charAt(0) || 'H'}
                  </span>
                </div>
                <div>
                  <p className="text-white font-semibold">Hosted by {listing.host_name}</p>
                  <p className="text-zinc-500 text-sm">Host</p>
                </div>
              </div>
              {/* Removed pre-booking message button - guests can only message during booking or after approval */}
            </div>

            {/* Description */}
            <div>
              <h2 className="text-xl font-serif text-white mb-4">About This Space</h2>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-line">
                {listing.description}
              </p>
            </div>

            {/* Full Address (if user has confirmed booking) */}
            {listing.address_full && (
              <div className="bg-green-500/10 border border-green-500/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={20} className="text-green-400" weight="fill" />
                  <span className="text-green-400 font-semibold">Booking Confirmed</span>
                </div>
                <p className="text-white">
                  <span className="text-zinc-400">Full Address: </span>
                  {listing.address_full}
                </p>
              </div>
            )}

            {/* Amenities */}
            {listing.amenities && listing.amenities.length > 0 && (
              <div>
                <h2 className="text-xl font-serif text-white mb-4">Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {listing.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2 text-zinc-300">
                      <CheckCircle size={16} className="text-[#D4AF37]" weight="fill" />
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Location Map */}
            <LocationMap 
              city={listing.location_city} 
              zipCode={listing.location_zip}
              isBooked={!!listing.address_full}
            />

            {/* House Rules */}
            {listing.rules && listing.rules.length > 0 && (
              <div>
                <h2 className="text-xl font-serif text-white mb-4">House Rules</h2>
                <ul className="space-y-2">
                  {listing.rules.map((rule, index) => (
                    <li key={index} className="text-zinc-300 flex items-start gap-2">
                      <span className="text-[#D4AF37]">•</span>
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Reviews */}
            <ReviewsSection
              reviews={reviews}
              averageRating={listing.average_rating}
              totalReviews={listing.total_reviews}
            />
          </div>

          {/* Right Column - Booking Widget */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              {/* Price Header */}
              <div className="bg-[#121212] border border-white/10 p-4 mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-[#D4AF37]">${listing.price_per_hour}</span>
                  <span className="text-zinc-500">/ hour</span>
                </div>
              </div>

              {/* Booking Calendar or Own Listing Message */}
              {isOwnListing ? (
                <div className="bg-[#121212] border border-white/10 p-6 text-center">
                  <House size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
                  <p className="text-white font-semibold mb-2">This is your listing</p>
                  <p className="text-zinc-500 text-sm mb-4">You cannot book your own space</p>
                  <Button
                    onClick={() => navigate('/host/dashboard')}
                    className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  >
                    Manage Listing
                  </Button>
                </div>
              ) : (
                <CalendarBookingWidget
                  listing={listing}
                  user={user}
                  token={token}
                  onBook={handleBookingSuccess}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
